import { useRef, useState } from 'react';
import { Ruler, Trash2, Upload } from 'lucide-react';
import type { ShapePoint } from '@peles/shared';
import { inputClass, labelClass } from './RoomShapePanels';
import type { CanvasMode } from './RoomShapeCanvas';
import type { RoomShapeEditorState } from './useRoomShapeEditor';
import type { useTraceDrawing } from './useTraceDrawing';

const buttonClass =
  'inline-flex items-center justify-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50';
const primaryClass =
  'inline-flex items-center justify-center gap-1 rounded-md bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700 disabled:opacity-50';

/** Scale a drawing so two marked points are `realFt` apart. */
export function calibratedFeetPerPixel(
  feetPerPixel: number,
  points: ShapePoint[],
  realFt: number,
): number | null {
  if (points.length < 2 || !(realFt > 0)) return null;
  const measured = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
  if (measured < 1e-6) return null;
  return feetPerPixel * (realFt / measured);
}

interface TracePanelProps {
  editor: RoomShapeEditorState;
  drawing: ReturnType<typeof useTraceDrawing>;
  mode: CanvasMode;
  setMode: (m: CanvasMode) => void;
  tracePoints: ShapePoint[];
  setTracePoints: (p: ShapePoint[]) => void;
  calibrationPoints: ShapePoint[];
  setCalibrationPoints: (p: ShapePoint[]) => void;
  /** Close the traced outline into the room's walls. */
  onFinishTrace: () => void;
}

/**
 * Trace the room from the venue's own drawing: upload it, mark one distance
 * you know so it is to scale, then click round the corners.
 */
export function TracePanel({
  editor,
  drawing,
  mode,
  setMode,
  tracePoints,
  setTracePoints,
  calibrationPoints,
  setCalibrationPoints,
  onFinishTrace,
}: TracePanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [realFt, setRealFt] = useState('');
  const bg = editor.shape.background;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const result = await drawing.upload(file);
    if (!result) return;
    // Until it is calibrated, stretch the drawing across the room as it stands.
    const widthFt = editor.measurements.widthFt || 100;
    editor.update((s) => ({
      ...s,
      background: {
        attachmentId: result.attachmentId,
        x: 0,
        y: 0,
        feetPerPixel: widthFt / Math.max(1, result.width),
        opacity: 0.6,
      },
    }));
    setCalibrationPoints([]);
    setMode('calibrate');
  };

  const applyCalibration = () => {
    if (!bg) return;
    const fpp = calibratedFeetPerPixel(bg.feetPerPixel, calibrationPoints, parseFloat(realFt));
    if (!fpp) return;
    // Scale about the drawing's corner: points on it move with the new scale.
    editor.update((s) => ({ ...s, background: { ...bg, feetPerPixel: fpp } }));
    setCalibrationPoints([]);
    setRealFt('');
    setTracePoints([]);
    setMode('trace');
  };

  return (
    <div className="space-y-3">
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        data-testid="trace-file-input"
        onChange={(e) => {
          void onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {!bg ? (
        <>
          <p className="text-xs text-gray-500">
            Have the venue&apos;s floor plan? Upload a photo, image or PDF of it and trace the walls
            over it.
          </p>
          <button
            type="button"
            className={primaryClass}
            disabled={drawing.uploading}
            onClick={() => fileRef.current?.click()}
          >
            <Upload size={14} /> {drawing.uploading ? 'Uploading…' : 'Upload a drawing'}
          </button>
        </>
      ) : (
        <>
          <ol className="space-y-2 text-sm">
            <li className={mode === 'calibrate' ? 'font-medium text-gray-900' : 'text-gray-600'}>
              <span className="mr-1 text-gray-400">1.</span>
              Mark a distance you know — click both ends of one wall or door.
            </li>
            <li className={mode === 'trace' ? 'font-medium text-gray-900' : 'text-gray-600'}>
              <span className="mr-1 text-gray-400">2.</span>
              Click each corner in turn. Click the first corner again to finish.
            </li>
          </ol>

          {mode === 'calibrate' && (
            <div className="space-y-2 rounded-md bg-amber-50 p-2" data-testid="calibration">
              <p className="text-xs text-amber-800">
                {calibrationPoints.length < 2
                  ? `Click ${calibrationPoints.length === 0 ? 'the first' : 'the second'} end of the distance.`
                  : 'How long is that, really?'}
              </p>
              {calibrationPoints.length >= 2 && (
                <label className="block">
                  <span className={labelClass}>Real distance (ft)</span>
                  <input
                    className={inputClass}
                    type="number"
                    min={0.1}
                    value={realFt}
                    onChange={(e) => setRealFt(e.target.value)}
                    autoFocus
                  />
                </label>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  className={primaryClass}
                  disabled={calibrationPoints.length < 2 || !(parseFloat(realFt) > 0)}
                  onClick={applyCalibration}
                >
                  Set scale
                </button>
                <button
                  type="button"
                  className={buttonClass}
                  onClick={() => {
                    setCalibrationPoints([]);
                    setMode('trace');
                  }}
                >
                  Skip
                </button>
              </div>
            </div>
          )}

          {mode === 'trace' && (
            <div className="space-y-2 rounded-md bg-primary-50 p-2" data-testid="tracing">
              <p className="text-xs text-primary-800">
                {tracePoints.length} corner{tracePoints.length === 1 ? '' : 's'} placed. Walls snap
                level or plumb while &quot;Right angles&quot; is on; Backspace removes the last
                corner.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={primaryClass}
                  disabled={tracePoints.length < 3}
                  onClick={onFinishTrace}
                >
                  Finish outline
                </button>
                <button
                  type="button"
                  className={buttonClass}
                  disabled={!tracePoints.length}
                  onClick={() => setTracePoints(tracePoints.slice(0, -1))}
                >
                  Undo corner
                </button>
                <button
                  type="button"
                  className={buttonClass}
                  onClick={() => {
                    setTracePoints([]);
                    setMode('edit');
                  }}
                >
                  Stop tracing
                </button>
              </div>
            </div>
          )}

          {mode === 'edit' && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={primaryClass}
                onClick={() => {
                  setTracePoints([]);
                  setMode('trace');
                }}
              >
                Trace the walls
              </button>
              <button
                type="button"
                className={buttonClass}
                onClick={() => {
                  setCalibrationPoints([]);
                  setMode('calibrate');
                }}
              >
                <Ruler size={14} /> Set scale
              </button>
            </div>
          )}

          <label className="block">
            <span className={labelClass}>Drawing visibility</span>
            <input
              type="range"
              className="w-full"
              min={0.1}
              max={1}
              step={0.05}
              aria-label="Drawing visibility"
              value={bg.opacity ?? 0.6}
              onChange={(e) =>
                editor.update(
                  (s) =>
                    s.background
                      ? { ...s, background: { ...s.background, opacity: Number(e.target.value) } }
                      : s,
                  {
                    coalesce: true,
                  },
                )
              }
              onPointerUp={editor.endGesture}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={buttonClass}
              disabled={drawing.uploading}
              onClick={() => fileRef.current?.click()}
            >
              <Upload size={14} /> Replace
            </button>
            <button
              type="button"
              className={buttonClass}
              onClick={() => {
                editor.update((s) => ({ ...s, background: null }));
                setMode('edit');
              }}
            >
              <Trash2 size={14} /> Remove drawing
            </button>
          </div>
          {drawing.loading && <p className="text-xs text-gray-500">Loading the drawing…</p>}
        </>
      )}
      {drawing.error && (
        <p className="text-xs text-red-600" role="alert">
          {drawing.error}
        </p>
      )}
    </div>
  );
}
